import { useEffect, useMemo, useState } from 'react';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { AmenityMatrix } from '@/components/admin/AmenityMatrix';
import { RankingRecomputeButton } from '@/components/admin/RankingRecomputeButton';
import { useAdminAmenities } from '@/hooks/useAdminAmenities';
import { useTriggerRefresh } from '@/hooks/useTriggerRefresh';
import { useTriggerRecompute } from '@/hooks/useTriggerRecompute';
import { useRecomputeStatus } from '@/hooks/useRecomputeStatus';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { AdminRefreshScope } from '@/types/db';

/**
 * `<AdminPage>` — Phase 09 ADMIN-01 entry point at /app/admin.
 *
 * Composes:
 *   - useAdminAmenities()  — county × category matrix (auto-refresh 30s)
 *   - useTriggerRefresh()  — POST /api/admin/refresh; on success invalidates
 *                            ['admin-amenities'] so the matrix re-fetches.
 *   - useTriggerRecompute() — POST /api/admin/recompute-rankings (RANK-04 #3)
 *   - useRecomputeStatus()  — polls status every 3s while in-flight.
 *
 * Single-operator surface (ADMIN-04 — no auth). Reachable directly at
 * /app/admin; the GlobalNav 'Admin' link routes here.
 *
 * Inline toast banner surfaces refresh successes + 409 concurrency errors.
 * Auto-dismisses after 4s; replaced when a new mutation fires. Toast text
 * is rendered via React children (escaped) — never via raw-HTML APIs.
 */
function scopeKey(scope: AdminRefreshScope): string {
  if (scope.scope === 'county') return `county:${scope.county}`;
  if (scope.scope === 'category') return `category:${scope.category}`;
  return 'all';
}

interface ToastState {
  kind: 'success' | 'error';
  text: string;
}

export function AdminPage() {
  // Document title (mirrors MapPage pattern)
  useEffect(() => {
    document.title = 'Admin — House Hunter';
  }, []);

  const matrix = useAdminAmenities();
  const triggerRefresh = useTriggerRefresh();
  const triggerRecompute = useTriggerRecompute();

  // Polling-enable flag: starts false, flips true on first user-fired
  // recompute this session. Stays true until next route mount so the
  // operator sees the final "Done" transition before polling stops.
  const [recomputeArmed, setRecomputeArmed] = useState(false);
  const recomputeStatus = useRecomputeStatus(recomputeArmed);

  // If the initial fetch shows recompute already running (e.g., another
  // operator triggered it via curl), arm polling automatically.
  useEffect(() => {
    if (recomputeStatus.data?.running === true) setRecomputeArmed(true);
  }, [recomputeStatus.data?.running]);

  // pendingMap derivation: while a refresh mutation is in-flight, mark its
  // scope as pending so the matrix disables the matching buttons.
  const pendingMap = useMemo<Record<string, boolean>>(() => {
    if (!triggerRefresh.isPending || !triggerRefresh.variables) return {};
    return { [scopeKey(triggerRefresh.variables as AdminRefreshScope)]: true };
  }, [triggerRefresh.isPending, triggerRefresh.variables]);

  // Toast surface
  const [toast, setToast] = useState<ToastState | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  function handleRefresh(scope: AdminRefreshScope) {
    triggerRefresh.mutate(scope, {
      onSuccess: (resp) => {
        setToast({ kind: 'success', text: `Refresh queued (job #${resp.job_id})` });
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          const body = err.body as { running_job_id?: number } | undefined;
          const id = body?.running_job_id;
          setToast({
            kind: 'error',
            text:
              id != null
                ? `Refresh already running for job #${id}`
                : 'Refresh already running',
          });
          return;
        }
        setToast({ kind: 'error', text: `Refresh failed: ${err.message}` });
      },
    });
  }

  function handleRecompute() {
    setRecomputeArmed(true);
    triggerRecompute.mutate(undefined, {
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          setToast({ kind: 'error', text: 'Recompute already running' });
          return;
        }
        setToast({ kind: 'error', text: `Recompute failed: ${err.message}` });
      },
    });
  }

  // ======================== render ========================
  return (
    <main
      id="main"
      className="min-h-screen bg-ocean-950 text-foam-50 pt-20 pb-12 px-6"
    >
      <div className="mx-auto max-w-6xl">
        <GlassPanel variant="strong" className="p-6 md:p-8">
          {/* Header band: h1 + intro + recompute CTA */}
          <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-foam-50">Amenity Refresh</h1>
              <p className="text-sm text-foam-50/70 mt-1">
                14 counties × 4 categories. Refresh per cell, per row, or per column.
                Recompute rankings after bulk amenity changes to update the dashboard.
              </p>
            </div>
            <div className="md:ml-auto">
              <RankingRecomputeButton
                status={recomputeStatus.data}
                pending={triggerRecompute.isPending}
                onTrigger={handleRecompute}
              />
            </div>
          </header>

          {/* Toast surface */}
          {toast && (
            <div
              role={toast.kind === 'error' ? 'alert' : 'status'}
              aria-live="polite"
              className={cn(
                'mb-4 px-4 py-2 rounded-lg border text-sm',
                toast.kind === 'success' &&
                  'bg-moss-500/10 border-moss-500/40 text-moss-500',
                toast.kind === 'error' &&
                  'bg-coral-500/10 border-coral-500/40 text-coral-500',
              )}
            >
              {toast.text}
            </div>
          )}

          {/* Matrix surface — loading / error / success */}
          {matrix.isPending && (
            <div className="py-16 text-center text-foam-50/70">Loading admin…</div>
          )}
          {matrix.isError && (
            <div className="py-16 text-center">
              <p className="text-coral-500 mb-4">Failed to load admin matrix</p>
              <button
                type="button"
                onClick={() => matrix.refetch()}
                className={cn(
                  'inline-flex items-center px-4 h-10 rounded-lg text-sm font-semibold',
                  'bg-foam-50/10 text-foam-50 hover:bg-foam-50/15',
                  'border border-white/10 hover:ring-1 hover:ring-bronze-500/40',
                  'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
                )}
              >
                Retry
              </button>
            </div>
          )}
          {matrix.data && (
            <div className="overflow-x-auto">
              <AmenityMatrix
                data={matrix.data}
                pendingMap={pendingMap}
                onRefresh={handleRefresh}
              />
            </div>
          )}
        </GlassPanel>
      </div>
    </main>
  );
}
